import * as React from 'react';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useBetreuer } from 'api/betreuer';
import { useKundeList } from 'api/kunde';
import { SelectOption } from 'formComponents/Select/types';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Select } from 'formComponents/Select/Select';
import { ValueType } from 'react-select';
import { Rechnung, RechnungFilter, useRechnungList } from 'api/rechnung';
import { RechnungItem } from 'components/Rechnung/RechnungItem';
import useDebouncedState from 'hooks/useDebouncedState';
import { RechnungPosition, RechnungPositionFilter, useRechnungPositionList } from 'api/rechnungPosition';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import { RechnungEdit, RechnungEditData } from 'components/Rechnung/RechnungEdit';
import { List } from '@fluentui/react/lib/List';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment-timezone';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { useHistory, useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl, decodeRecordNr } from 'utils/formatter';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';
import { RechnungPositionenHeader } from 'components/Rechnung/RechnungPositionenHeader';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { RechnungStatusMulti } from 'formComponents/lookups/RechnungStatusMulti';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 150px 170px 170px 5fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall1: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 150px 180px 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall2: {
        display: 'grid',
        gridTemplateColumns: '170px 170px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    auNr?: string;
    status: string;
    statusList: number[];
    betreuer: SelectOption;
    datumVon?: string;
    datumBis?: string;
    lArt: string;
    searchText: string;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    auNr: '',
    status: '__all__',
    statusList: [],
    betreuer: alleBetreuerOption,
    datumVon: undefined,
    datumBis: undefined,
    lArt: '__all__',
    searchText: '',
};

export const RechnungView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'RechnungView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 2100px)` });
    const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1750px)` });
    const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;

    const rechnungEditData = React.useMemo<RechnungEditData | null>(() => {
        if (urlRecordNr) {
            return {
                rNr: decodeRecordNr(urlRecordNr),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle === 'AD') {
            return {
                ...defaultFilterValues,
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<RechnungFilter>(() => {
        return {
            kNr,
            betreuer:
                debouncedFilterValues.betreuer.value === '__all__'
                    ? undefined
                    : parseInt(debouncedFilterValues.betreuer.value, 10),
            // status: debouncedFilterValues.status === '__all__' ? undefined :
            // parseInt(debouncedFilterValues.status, 10),
            statusList: debouncedFilterValues.statusList,
            nrSearch: debouncedFilterValues.nr,
            auNrSearch: debouncedFilterValues.auNr,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            lArt: debouncedFilterValues.lArt === '__all__' ? undefined : debouncedFilterValues.lArt,
            searchText: debouncedFilterValues.searchText,
            withKunde: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const rechnungList = useRechnungList(filter);

    const positionenFilter = React.useMemo<RechnungPositionFilter>(() => {
        if (rechnungList.isLoading || rechnungList.isLoading || rechnungList.isError) {
            return {};
        }

        return {
            rNrList: rechnungList.data?.map((rechnung) => rechnung.nr) || [],
        };
    }, [rechnungList]);

    const rechnungPositionList = useRechnungPositionList(positionenFilter, { enabled: false });

    React.useEffect(() => {
        if (positionenFilter.rNrList) {
            rechnungPositionList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionenFilter]);

    const rechnungPositionenByANr = React.useMemo<{ [key: string]: RechnungPosition[] }>(() => {
        if (
            rechnungPositionList.isLoading ||
            rechnungPositionList.isLoading ||
            rechnungPositionList.isError ||
            (rechnungPositionList.data?.length || 0) === 0
        ) {
            return {};
        }

        const newRechnungPositionenByANr: { [key: string]: RechnungPosition[] } = {};

        rechnungPositionList.data?.forEach((rechnungPosition) => {
            if (!newRechnungPositionenByANr[rechnungPosition.rNr]) {
                newRechnungPositionenByANr[rechnungPosition.rNr] = [];
            }

            newRechnungPositionenByANr[rechnungPosition.rNr].push(rechnungPosition);
        });

        return newRechnungPositionenByANr;
    }, [rechnungPositionList]);

    const onRenderCell = React.useCallback(
        (rechnung: Rechnung | undefined): JSX.Element => {
            if (!rechnung) {
                return <div />;
            }

            return (
                <RechnungItem
                    key={rechnung.nr}
                    rechnung={rechnung}
                    withKunde={!urlKundeNr}
                    viewMode={viewMode}
                    withHeader={false}
                    rechnungPositionen={rechnungPositionenByANr[rechnung.nr] || []}
                    editRechnung={() => history.push(createRecordUrl('rechnungen', urlKundeNr, rechnung.nr))}
                />
            );
        },
        [rechnungPositionenByANr, urlKundeNr, viewMode, history],
    );

    const renderDatumVon = () => (
        <FormDatePicker
            placeholder="Datum (von)"
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            placeholder="Datum (bis)"
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={viewStyles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, {
                [viewStyles.twoSearchBarLines]: isSmallScreen,
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    {rechnungEditData?.rNr ? 'Rechnung ' + rechnungEditData?.rNr : 'Rechnung'}
                </title>
            </Helmet>
            <div className={isSmallScreen ? styles.searchBarSmall1 : styles.searchBar}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Rechnung-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Auftrag-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            auNr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <LieferArt
                    value={filterValues.lArt || ''}
                    addAll
                    menuPlacement="bottom"
                    renderLabel="placeholder"
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            lArt: newValue,
                        });
                    }}
                />
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || alleBetreuerOption,
                        });
                    }}
                />

                {isSmallScreen || renderDatumVon()}
                {isSmallScreen || renderDatumBis()}

                <RechnungStatusMulti
                    value={filterValues.statusList.map((v) => v.toString())}
                    renderLabel="placeholder"
                    onChange={(newValues) => {
                        setFilterValues({
                            ...filterValues,
                            statusList: newValues.map((v) => parseInt(v, 10)),
                        });
                    }}
                />

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>

                {auth.rolle !== 'AD' && (
                    <PrimaryButton
                        onClick={() => history.push(createRecordUrl('rechnungen', urlKundeNr, 'neu'))}
                        text="Neue Rechnung"
                    />
                )}
            </div>

            {isSmallScreen && (
                <div className={styles.searchBarSmall2}>
                    {renderDatumVon()}
                    {renderDatumBis()}
                </div>
            )}

            {rechnungList.isLoading || rechnungList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainer}>
                    {rechnungList?.data && rechnungList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Rechnungen.</div>
                    ) : (
                        <>
                            {viewMode === 'short' && (
                                <div className={viewStyles.listContainer}>
                                    <table className={formStyles.stickyPositionenHeaderTable}>
                                        <RechnungPositionenHeader />
                                    </table>
                                </div>
                            )}
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div
                                        style={{ width, height: height - 25, overflowX: 'hidden', overflowY: 'scroll' }}
                                    >
                                        <List
                                            key={viewMode + (rechnungPositionList.isSuccess ? 1 : 0)}
                                            items={rechnungList.data || []}
                                            onRenderCell={onRenderCell}
                                        />
                                    </div>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
            )}
            {rechnungEditData !== null && (
                <RechnungEdit
                    defaultKNr={rechnungEditData.kNr}
                    rNr={rechnungEditData.rNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('rechnungen', urlKundeNr));
                        if (refetch) {
                            rechnungPositionList.refetch().then(() => {
                                rechnungList.refetch().then();
                            });
                        }
                    }}
                    onReopen={(newRNr: string) => {
                        history.push(createRecordUrl('rechnungen', urlKundeNr));
                        rechnungPositionList.refetch().then(() => {
                            rechnungList.refetch().then(() => {
                                history.push(createRecordUrl('rechnungen', urlKundeNr, newRNr));
                            });
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
